import { useNavigation, useRoute } from '@react-navigation/native';
import {
  createStackNavigator,
  StackScreenProps,
} from '@react-navigation/stack';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import { Alert } from '@components/Alert';
import SecondaryHeader from '@components/Headers/SecondaryHeader';
import { SimpleHeader } from '@components/Headers/SimpleHeader.web';
import { AddMemberType } from '@components/Invite/SelectMembersList';
import { WebModal } from '@components/Modals/WebModal.web';
import {
  useCreateTeamMemberInvitationMutation,
  useUpdateTeamMembersMutation,
  TeamRole,
  User,
  GetTeamDocument,
} from '@graphql/generated';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { ProfileStackScreenProps } from '@navigation/profile/profile-stack.web';
import addMembersSchema from '@schemas/addMembersSchema';
import { AddMembers } from '@screens/Members/AddMembers';
import { EditNewMemberRoles } from '@screens/Members/EditNewMemberRoles';
import { SelectMembersFromTeam } from '@screens/Members/SelectMembersFromTeam';
import { defaultTeamRole, selectableTeamRoles } from '@utils/teams';
import { splitTeamMemberChanges } from '@utils/teams/splitTeamMemberChanges';

export type FormValues = InferType<typeof addMembersSchema>;
type AddTeamMembersModalStackParamsList = {
  'add-members': undefined;
  'edit-new-member-roles': undefined;
  'select-members-from-team': undefined;
};

export type AddMembersStackScreenProps<
  T extends keyof AddTeamMembersModalStackParamsList,
> = StackScreenProps<AddTeamMembersModalStackParamsList, T>;

const Stack = createStackNavigator<AddTeamMembersModalStackParamsList>();

const AddMembersHeader = () => (
  <SecondaryHeader title='Add Members' searchable={false} marginTop='m' />
);

const SelectMembersFromTeamHeader = () => <SimpleHeader title='Team' />;

const createAssignRoleHeader = (title: string) => () => (
  <SecondaryHeader title={title} searchable={false} marginTop='m' />
);

interface AddTeamMembersModalStackProps {
  initialRouteName?: keyof AddTeamMembersModalStackParamsList;
}

export const AddTeamMembersModalStack: React.FC<
  AddTeamMembersModalStackProps
> = ({ initialRouteName }) => {
  const { t } = useTranslation();
  const [selected] = useState<AddMemberType[]>();

  const { params: { teamUUId = '', initialUsers } = {} } =
    useRoute<ProfileStackScreenProps<'add-team-members-stack'>['route']>();

  const navigation = useNavigation();

  // Full (withMembers) variant: team.users tells existing members, who get a
  // role update, apart from new ones, who get an invitation (TA-11187).
  const { team } = useTeamFromQuery({
    teamUUId,
    includePending: true,
  });

  const refetchQueries = teamUUId
    ? [
        {
          query: GetTeamDocument,
          variables: { id: teamUUId, includePending: true },
        },
      ]
    : [];

  const [updateTeamMembers] = useUpdateTeamMembersMutation({
    refetchQueries,
  });
  const [createTeamMemberInvitation] = useCreateTeamMemberInvitationMutation({
    refetchQueries,
  });

  const addMembersToTeam = (values: FormValues) => {
    if (!team?.id) return;

    const { roleUpdates, invitations } = splitTeamMemberChanges(
      values.users,
      team.users.map((u) => u.id)
    );

    Promise.all([
      roleUpdates.length &&
        updateTeamMembers({
          variables: { teamId: team.id, attributes: roleUpdates },
        }),
      invitations.length &&
        createTeamMemberInvitation({
          variables: { id: team.id, teamMembers: invitations },
        }),
    ]).catch((error: Error) => Alert.alert(t('shared:error'), error.message));
  };

  const submitForm = (values: FormValues) => {
    addMembersToTeam(values);
    navigation.goBack();
  };

  const initialValues: FormValues = {
    users: initialUsers ?? [],
    selectedUserIds: selected?.map((u) => u.id) || [],
  };

  const [showMemberModal, setShowMemberModal] = useState(true);

  return (
    <WebModal
      accessibilityLabel='Team - Add Members'
      onClose={() => {
        setShowMemberModal(false);
      }}
      visible={showMemberModal}
      width={504}
      marginTop='m'
      height={514}>
      <Formik
        initialValues={initialValues}
        onSubmit={submitForm}
        validationSchema={addMembersSchema}
        validateOnMount>
        {({ setFieldValue, values, submitForm, isSubmitting }) => {
          const { selectedUserIds: selectedUserIdsRaw, users } = values;
          const selectedUserIds = selectedUserIdsRaw?.filter(
            (s) => !!s
          ) as string[];

          return (
            <Stack.Navigator initialRouteName={initialRouteName || undefined}>
              <Stack.Screen
                name='add-members'
                options={{
                  header: AddMembersHeader,
                }}>
                {() => (
                  <AddMembers
                    hideTeamTab={true}
                    disabled={!users.length}
                    onNextPress={() =>
                      navigation.navigate('edit-new-member-roles')
                    }
                    showTaskTagSupportUser={false}
                    selectedUserIds={selectedUserIds}
                    users={users}
                    updateUsers={(newUsers: AddMemberType[]) => {
                      setFieldValue(
                        'users',
                        newUsers.map((u) => {
                          return {
                            ...u,
                            role: u.role || defaultTeamRole,
                          };
                        })
                      );
                    }}
                  />
                )}
              </Stack.Screen>
              <Stack.Screen
                name='select-members-from-team'
                options={{
                  header: SelectMembersFromTeamHeader,
                }}>
                {() => (
                  <SelectMembersFromTeam
                    onSubmit={() =>
                      navigation.navigate('edit-new-member-roles')
                    }
                    disabled={!users.length}
                    users={users}
                    updateUsers={(newUsers: AddMemberType[]) => {
                      setFieldValue(
                        'users',
                        newUsers.map((u) => {
                          return {
                            ...u,
                            role: u.role || defaultTeamRole,
                          };
                        })
                      );
                    }}
                  />
                )}
              </Stack.Screen>

              <Stack.Screen
                name='edit-new-member-roles'
                options={{
                  header: createAssignRoleHeader(
                    t('models:addMembers.assignRole')
                  ),
                }}>
                {() => (
                  <EditNewMemberRoles<TeamRole>
                    roles={selectableTeamRoles}
                    updateMemberRole={(id: User['id'], role: TeamRole) => {
                      setFieldValue(
                        'users',
                        users.map((u) => {
                          return {
                            ...u,
                            role: u.id === id ? role : u.role,
                          };
                        })
                      );
                    }}
                    isTaskAssignee={false}
                    usersAndRoles={users
                      .filter(
                        (u) =>
                          !initialUsers?.some(
                            (initialUser) => initialUser.id === u.id
                          )
                      )
                      .map((u) => ({
                        user: u,
                        role: u.role,
                      }))}
                    goBack={true}
                    teamUUId={teamUUId}
                    onSubmit={submitForm}
                    isSubmitting={isSubmitting}
                  />
                )}
              </Stack.Screen>
            </Stack.Navigator>
          );
        }}
      </Formik>
    </WebModal>
  );
};
